import { useCallback, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import { createApiResponse } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { numberComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import useTabSearchParams from "@entrilia/shared/hooks/useTabSearchParams";
import adminApi from "../../../../../api/adminApi";
import type { Fundraising } from "../../../../../api/types/fundraisingTypes";
import useClientContext from "../../../../../context/useClientContext";
import PageTabPanel from "../../../../common/PageTabPanel";
import PageTabs from "../../../../common/PageTabs";
import FundraisingDialogs from "../dialogs/FundraisingDialogs";
import type { DialogState, FundraisingStatusTransition } from "../fundraisingsPageTypes";
import { detailsPageTabLabels, detailsPageTabs } from "../fundraisingsPageTypes";
import FundraisingAccessConfig from "./fundraising-access/FundraisingAccessConfig";
import FundraisingDocuments from "./fundraising-documents/FundraisingDocuments";
import FundraisingCategoriesConfig from "./FundraisingCategoriesConfig";
import FundraisingContentConfig from "./FundraisingContentConfig";
import { FundraisingDetailsPageContextProvider } from "./FundraisingDetailsPageContext";
import FundraisingDetailsPageHeader from "./FundraisingDetailsPageHeader";
import FundraisingDocumentActivity from "./FundraisingDocumentActivity";
import FundraisingNotificationsConfig from "./FundraisingNotificationsConfig";
import FundraisingOverview from "./FundraisingOverview";

const FundraisingDetailsPage = () => {
  usePageTitle("Fundraising Details");

  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermissions, accessCategories, refreshAccessCategories } = useClientContext();
  const [dialogState, setDialogState] = useState<DialogState>({});

  const { selectedTab, onTabChange } = useTabSearchParams(detailsPageTabs);

  const getFundraisingDetails = useCallback(
    async () => (id ? adminApi.getFundraisingById(id) : Promise.resolve(createApiResponse(undefined))),
    [id]
  );

  const [
    fundraising,
    fetchFundraisingError,
    { isFetching: isFetchingFundraising, setData: setFundraising, fetch: refreshFundraising },
  ] = useFetch(getFundraisingDetails);

  const [portalSettings, fetchSettingsError, { isFetching: isFetchingSettings }] = useFetch(
    adminApi.getInvestorPortalSettings
  );

  if (!id) {
    return <Navigate to="../" />;
  }

  const fetchError = fetchFundraisingError || fetchSettingsError;
  const isLoading = isFetchingFundraising || isFetchingSettings;

  if (fetchError) {
    logError(fetchError, "[FundraisingDetailsPage] fetch");
    return <DataLoadingFailed title="Failed to load fundraising details" />;
  }

  if (isLoading || fundraising === undefined) {
    return <InlineLoader />;
  }

  const handleRename = () => {
    if (fundraising) {
      setDialogState({
        openDialog: "rename",
        fundraisingId: fundraising.id,
        fundraisingEtag: fundraising.etag,
        fundraisingName: fundraising.name,
      });
    }
  };

  const handleStatusChange = (statusTransition: FundraisingStatusTransition) => {
    setDialogState({
      openDialog: "change_status",
      statusTransition,
      fundraisingId: fundraising.id,
      fundraisingEtag: fundraising.etag,
      fundraisingName: fundraising.name,
      fundId: fundraising.fund?.id,
      areNotificationsSetUp: fundraising.areNotificationsSetUp,
    });
  };

  const handleConfirmSave = (onSaveConfirmed: () => void) => {
    if (fundraising.status !== "Live") {
      onSaveConfirmed();
      return;
    }

    setDialogState({
      openDialog: "confirm_save",
      onSaveConfirmed,
      fundraisingId: fundraising.id,
      fundraisingName: fundraising.name,
    });
  };

  const handleDelete = () => {
    setDialogState({
      openDialog: "delete",
      fundraisingId: fundraising.id,
      fundraisingName: fundraising.name,
    });
  };

  const handleDialogClose = () => setDialogState({});

  const handleDeleted = () => {
    handleDialogClose();
    navigate("..");
  };

  const handleUpdated = (updatedFundraising: Fundraising) => {
    handleDialogClose();
    setFundraising(updatedFundraising);
  };

  const hasEditPermissions = hasPermissions(["ManageFundraisings"]);

  const fundraisingCategories = accessCategories
    .filter((c) => c.type === "Fundraising")
    .sort(numberComparerBy((c) => c.sortOrder));

  const isPageDeactivatedOnPortal =
    portalSettings !== undefined &&
    !portalSettings.settings.enabledFeatures?.includes("Fundraising") &&
    !portalSettings.settings.enabledFeatures?.includes("All");

  const displayedTabs =
    fundraising.status !== "Draft" ? [...detailsPageTabs] : detailsPageTabs.filter((tab) => tab !== "activity");

  const tabLabels =
    fundraising.status !== "Draft"
      ? [...detailsPageTabLabels]
      : detailsPageTabLabels.filter((label) => label !== "Activity");

  return (
    <FundraisingDetailsPageContextProvider
      fundraising={fundraising}
      hasEditPermissions={hasEditPermissions}
      isContentEditable={hasEditPermissions && fundraising.status !== "Completed"}
      fundraisingCategories={fundraisingCategories}
      isPageDeactivatedOnPortal={isPageDeactivatedOnPortal}
      onRename={handleRename}
      onStatusChange={handleStatusChange}
      onConfirmSave={handleConfirmSave}
      onDelete={handleDelete}
      onTabChange={onTabChange}
      onUpdated={handleUpdated}
      refreshCategories={refreshAccessCategories}
      refreshFundraising={refreshFundraising}
    >
      <FundraisingDetailsPageHeader />
      <PageTabs tabs={displayedTabs} labels={tabLabels} value={selectedTab} onChange={onTabChange}>
        <PageTabPanel value="overview" containerWidth="lg">
          <FundraisingOverview />
        </PageTabPanel>
        <PageTabPanel value="categories" containerWidth="lg">
          <FundraisingCategoriesConfig />
        </PageTabPanel>
        <PageTabPanel value="access" containerWidth="lg">
          <FundraisingAccessConfig />
        </PageTabPanel>
        <PageTabPanel value="content" containerWidth="lg">
          <FundraisingContentConfig />
        </PageTabPanel>
        <PageTabPanel value="documents" containerWidth="xl">
          <FundraisingDocuments />
        </PageTabPanel>
        {fundraising.status !== "Draft" && (
          <PageTabPanel value="activity" fillHeight>
            <FundraisingDocumentActivity />
          </PageTabPanel>
        )}
        <PageTabPanel value="notifications" containerWidth="lg">
          <FundraisingNotificationsConfig />
        </PageTabPanel>
      </PageTabs>

      <FundraisingDialogs
        dialogState={dialogState}
        onClose={handleDialogClose}
        onCreated={() => handleDialogClose()}
        onUpdated={handleUpdated}
        onDeleted={handleDeleted}
      />
    </FundraisingDetailsPageContextProvider>
  );
};

export default FundraisingDetailsPage;
